import { FloatingPosition, Popover } from '@mantine/core';
import React from 'react';

export interface IDisclosurePopoverProps {
  /** Accessible name of the trigger, e.g. 'Show 3 more'. */
  label: string;
  /** The revealed content. */
  content: React.ReactNode;
  /**
   * The trigger. Must render a single focusable element (a button —
   * ActionIcon, UnstyledButton, `Badge component="button"`) that forwards
   * its ref to that element: `Popover.Target` clones a ref onto it, and a
   * non-forwarding component leaves floating-ui with a null reference (all
   * lib buttons/action-icons forward refs). It must also not carry its own
   * tooltip — e.g. `label` on a `BasicActionIcon` stacks a Mantine Tooltip
   * on top of this popover on hover — DisclosurePopover supplies the
   * accessible name itself. Its own event handlers are replaced by the
   * disclosure's.
   */
  children: React.ReactElement;
  width?: number | string;
  position?: FloatingPosition;
}

// Debounce policy for the whole library: a pointer scanning across a dense
// table (e.g. a row of rate cells) must not machine-gun popovers open, so
// opening waits a beat and closing waits a shorter beat to tolerate the
// trigger-to-dropdown gap. Keyboard/tap are deliberate acts and stay instant.
const HOVER_OPEN_DELAY_MS = 150;
const HOVER_CLOSE_DELAY_MS = 100;

const FOCUSABLE_SELECTOR =
  'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])';

/** @registry The library's single hover/focus disclosure: a focusable trigger that reveals a popover on debounced hover (150ms open / 100ms close, so scanning a pointer across a dense row doesn't flash popovers) or instant focus/tap. Interactive content is fully supported: ArrowDown from the trigger moves focus into the dropdown, Escape from either the trigger or the dropdown closes it and returns focus to the trigger, and blur only closes the popover once focus leaves both the trigger and the dropdown. Use instead of Mantine HoverCard, which is unreachable without a mouse. */
export const DisclosurePopover: React.FC<IDisclosurePopoverProps> = ({
  label,
  content,
  children,
  width,
  position,
}) => {
  const [opened, setOpened] = React.useState(false);
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  // Popover.Target clones its own ref onto the trigger element, so this is
  // populated from the trigger's own focus and mouseenter handlers rather
  // than a second clone (a second cloneElement here would just overwrite
  // Popover.Target's ref). Both open paths must set it: focus covers
  // keyboard/tap, mouseenter covers hover — without the latter, hovering
  // open then tabbing into the dropdown leaves it null and Escape can't
  // return focus anywhere.
  const triggerRef = React.useRef<HTMLElement | null>(null);
  // Escape's `triggerRef.current?.focus()` below is a genuine focus change
  // (the dropdown, not the trigger, has focus at that point), which
  // synchronously re-enters the trigger's own onFocus handler. Without this
  // guard that would call openNow() and immediately reopen what Escape just
  // closed.
  const restoringFocusRef = React.useRef(false);

  const cancel = () => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const openNow = () => {
    cancel();
    setOpened(true);
  };

  const closeNow = () => {
    cancel();
    setOpened(false);
  };

  const openAfterDelay = () => {
    cancel();
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      setOpened(true);
    }, HOVER_OPEN_DELAY_MS);
  };

  const closeAfterDelay = () => {
    cancel();
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      setOpened(false);
    }, HOVER_CLOSE_DELAY_MS);
  };

  React.useEffect(() => cancel, []);

  return (
    <Popover
      width={width}
      position={position}
      withArrow
      shadow="md"
      trapFocus={false}
      withinPortal
      opened={opened}
      onChange={setOpened}
      // Hover/focus disclosures should react instantly, not fade in on a
      // timer — and Mantine's default 150ms fade defers the dropdown mount
      // behind a requestAnimationFrame, which is otherwise indistinguishable
      // from content never mounting at all in a synchronous assertion.
      transitionProps={{ duration: 0 }}
    >
      <Popover.Target>
        {React.cloneElement(children, {
          'aria-label': label,
          onMouseEnter: (event: React.MouseEvent) => {
            triggerRef.current = event.currentTarget as HTMLElement;
            openAfterDelay();
          },
          onMouseLeave: closeAfterDelay,
          onFocus: (event: React.FocusEvent) => {
            triggerRef.current = event.currentTarget as HTMLElement;
            if (restoringFocusRef.current) return;
            openNow();
          },
          onBlur: (event: React.FocusEvent) => {
            const nextFocus = event.relatedTarget as Node | null;
            if (dropdownRef.current?.contains(nextFocus)) return;
            closeNow();
          },
          onKeyDown: (event: React.KeyboardEvent) => {
            if (event.key === 'Escape') {
              closeNow();
              return;
            }
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              openNow();
              // The dropdown mounts synchronously with `opened`, but its
              // portal content isn't in the DOM until after this render
              // commits — a microtask is enough since transitions are
              // disabled (duration: 0).
              queueMicrotask(() => {
                const focusable =
                  dropdownRef.current?.querySelector<HTMLElement>(
                    FOCUSABLE_SELECTOR,
                  );
                focusable?.focus();
              });
            }
          },
        })}
      </Popover.Target>
      <Popover.Dropdown
        ref={dropdownRef}
        onMouseEnter={openNow}
        onMouseLeave={closeAfterDelay}
        onBlur={(event: React.FocusEvent) => {
          const nextFocus = event.relatedTarget as Node | null;
          if (
            dropdownRef.current?.contains(nextFocus) ||
            triggerRef.current?.contains(nextFocus) ||
            triggerRef.current === nextFocus
          ) {
            return;
          }
          closeNow();
        }}
        onKeyDown={(event: React.KeyboardEvent) => {
          if (event.key === 'Escape') {
            closeNow();
            restoringFocusRef.current = true;
            triggerRef.current?.focus();
            restoringFocusRef.current = false;
          }
        }}
      >
        {content}
      </Popover.Dropdown>
    </Popover>
  );
};
